import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import {
  LucideAngularModule,
  ShoppingCartIcon,
  ChevronLeftIcon,
  ZoomInIcon,
  InfoIcon,
  Share2Icon,
  CheckIcon,
} from 'lucide-angular';
import { MainLayout } from '../../../layouts/main-layout';
import { CartService } from '../../../services/cart.service';
import { NotificationService } from '../../../services/notification.service';
import { ProductService } from '../../../services/product.service';
import { ProductTrackingService } from '../../../services/product-tracking.service';
import { CategoriesService } from '../../../services/categories.service';
import { PriceFormattingService } from '../../../services/price-formatting.service';
import { SeoService } from '../../../services/seo.service';
import { ProductUtilsService } from '../../../services/product-utils.service';
import { getProductCategorySlug } from '../../../utils/products/get-category-from-product';
import type { Producto } from '../../../types/product.types';
import type { Category } from '../../../types/category.types';
import { VehicleCompatibilityComponent } from '../../../components/shop/product-card/vehicle-compatibility.component';
import { DeliveryTypeByStore } from '../../../components/shop/delivery-type-by-store/delivery-type-by-store';
import { ImageZoomComponent } from '../../../components/shared/image-zoom/image-zoom';
import { ProductPrice } from '../../../components/shop/price/price';
import { CoreDeposit } from '../../../components/shop/core-deposit/core-deposit';
import { WarrantyGuard } from '../../../components/shop/warranty-guard/warranty-guard';
import { VehicleGuideModalComponent } from '../../../components/shop/vehicle-guide-modal/vehicle-guide-modal.component';
import { MarginWrapper } from '../../../layouts/margin-wrapper';
import { DialogModule } from 'primeng/dialog';
import { smartTitle } from '../../../utils/pipes/smart-capitalize';
import { VehicleService } from '../../../services/vehicle.service';
import { firstValueFrom } from 'rxjs';
import { toSlug } from '../../../utils/slugs/slug.utils';

@Component({
  selector: 'allas-producto-detalle',
  imports: [
    MainLayout,
    CommonModule,
    RouterModule,
    FormsModule,
    LucideAngularModule,
    VehicleCompatibilityComponent,
    DeliveryTypeByStore,
    ImageZoomComponent,
    ProductPrice,
    CoreDeposit,
    WarrantyGuard,
    VehicleGuideModalComponent,
    MarginWrapper,
    DialogModule,
  ],
  templateUrl: 'producto-detalle.component.html',
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
})
export class ProductoDetalle implements OnInit {
  // Services
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private cartService = inject(CartService);
  private notificationService = inject(NotificationService);
  private productService = inject(ProductService);
  private productTrackingService = inject(ProductTrackingService);
  private categoriesService = inject(CategoriesService);
  private priceFormattingService = inject(PriceFormattingService);
  private seoService = inject(SeoService);
  private document = inject(DOCUMENT);
  private platformId = inject(PLATFORM_ID);
  private titleService = inject(Title);
  private metaService = inject(Meta);
  readonly productUtilsService = inject(ProductUtilsService);
  readonly vehicleService = inject(VehicleService);

  // Icons
  readonly chevronLeftIcon = ChevronLeftIcon;
  readonly zoomInIcon = ZoomInIcon;
  readonly shoppingCartIcon = ShoppingCartIcon;
  readonly infoIcon = InfoIcon;
  readonly shareIcon = Share2Icon;
  readonly checkIcon = CheckIcon;

  // State
  loading = signal(true);
  product = signal<Producto | null>(null);
  selectedImage = signal<string>('');
  quantity = signal(1);
  selectedSize = signal('1');
  breadcrumbs = signal<Category[]>([]);
  showEspecificationsDialog = false;
  showShareDialog = false;
  vehiculoSlug = signal<string | null>(null);
  vehiculoTextFromSlug = signal<string | null>(null);
  defaultVehicle = signal<string | null>(null);
  loadingVehicleCompatibility = signal(true);
  myVehicleFitsWithProduct = signal<boolean>(false);
  linkCopied = signal(false);
  liveDataLoaded = signal(true);
  toSlug = toSlug;
  descriptionSeoOrganizada = signal({
    title: '',
    description: '',
    bullets: [] as string[],
  });

  // Computed values
  readonly productCategorySlug = computed(() => {
    const currentProduct = this.product();
    return currentProduct ? getProductCategorySlug(currentProduct) : null;
  });

  readonly warrantyYears = computed(() => {
    const currentProduct = this.product();
    return currentProduct
      ? this.productUtilsService.getWarrantyYears(currentProduct.marca)
      : 0;
  });

  readonly currentVehicleGuide = computed(() => {
    const currentProduct = this.product();
    return currentProduct?.guia || [];
  });

  readonly attributesToDisplay = computed(() => {
    const currentProduct = this.product();
    return currentProduct
      ? this.productUtilsService.getDisplayableAttributes(currentProduct)
      : [];
  });

  constructor() {
    this.subscribeToVehicleService();
  }

  ngOnInit() {
    this.route.params.subscribe((params) => {
      const slug = params['slug'];
      const vehiculoSlug = params['vehiculo'];
      if (vehiculoSlug) {
        this.vehiculoSlug.set(vehiculoSlug);
      }
      if (slug) {
        this.loadProduct(slug);
      }
    });
  }

  private setVehiculoTextFromSlug(slug: string) {
    const vehiculoSlugInApplication =
      this.checkIfVehiculoSlugIsInApplication(slug);

    if (!vehiculoSlugInApplication) return;

    const vehiculoText = slug
      .split('-')
      .map((word) => smartTitle(word))
      .join(' ');
    this.vehiculoTextFromSlug.set(vehiculoText);
  }

  private checkIfVehiculoSlugIsInApplication(slug: string) {
    const [marca, modelo, anio] = slug
      .split('-')
      .map((word) => word?.toUpperCase() || '');

    const product = this.product();

    if (!product) return false;

    const vehiculoInApplication = product.guia_tree?.some(
      (guia) =>
        guia.marca.toUpperCase() === marca &&
        guia.modelos.some(
          (modeloGuia) =>
            modeloGuia.modeloRaw.toUpperCase() === modelo &&
            this.isYearInRange(anio, modeloGuia.rangos)
        )
    );

    return vehiculoInApplication;
  }

  private isYearInRange(year: string, ranges: string[]): boolean {
    return ranges.some((range) => {
      // Si el rango es un año individual (ej: '2021')
      if (!range.includes('-')) {
        return range.trim() === year;
      }

      // Si el rango es un rango de años (ej: '2020 - 2025')
      const [startYear, endYear] = range
        .split('-')
        .map((y) => parseInt(y.trim()));
      const yearNumber = parseInt(year);

      return yearNumber >= startYear && yearNumber <= endYear;
    });
  }

  private loadProduct(slug: string) {
    this.loading.set(true);

    this.productService.getProductBySlug(slug).subscribe({
      next: (response) => {
        if (response) {
          this.handleProductLoaded(response);
          this.setVehiculoTextFromSlug(this.vehiculoSlug() || '');
        } else {
          this.handleProductNotFound();
        }
        this.loading.set(false);
      },
      error: (error) => {
        this.handleProductError(error);
        this.loading.set(false);
      },
    });
  }

  private handleProductLoaded(product: Producto) {
    this.product.set(product);
    this.setInitialImage(product);
    this.loadBreadcrumbs(product);
    this.productTrackingService.logProductView(product);
    this.seoService.setProductMetaTags(product, this.breadcrumbs());
    this.setMyVehicleFitsWithProduct();
    this.setSeoDescriptionOrganized(product);
    this.fetchLiveData(product);
  }

  private fetchLiveData(product: Producto) {
    if (!isPlatformBrowser(this.platformId)) return;

    this.liveDataLoaded.set(false);

    this.productService.getLiveData(product.sku).subscribe({
      next: (liveData) => {
        if (!liveData) {
          this.liveDataLoaded.set(true);
          return;
        }
        this.product.update((current) => {
          if (!current) return current;
          return {
            ...current,
            precio: liveData.precio,
            inventarios: liveData.inventarios,
          };
        });
        this.liveDataLoaded.set(true);
      },
      error: (error) => {
        console.error('Error fetching live data:', error);
        this.liveDataLoaded.set(true);
      },
    });
  }

  private setSeoDescriptionOrganized(product: Producto) {
    const seoDescription = product.seoDescription;
    if (!seoDescription) return;

    seoDescription.forEach((description) => {
      if (description.description_code === 'MKT') {
        this.descriptionSeoOrganizada.update((prev) => ({
          ...prev,
          title: description.description,
        }));
      }
      if (description.description_code === 'DES') {
        this.descriptionSeoOrganizada.update((prev) => ({
          ...prev,
          description: description.description,
        }));
      }
      if (description.description_code === 'FAB') {
        this.descriptionSeoOrganizada.update((prev) => ({
          ...prev,
          bullets: [...prev.bullets, description.description],
        }));
      }
    });
  }

  private subscribeToVehicleService() {
    this.vehicleService.vehicles$.subscribe(() => {
      const product = this.product();
      if (!product) {
        return;
      }

      const defaultVehicle = this.vehicleService.getDefaultVehicle();
      this.defaultVehicle.set(defaultVehicle?.alias || null);
      this.setMyVehicleFitsWithProduct();
    });
  }

  private async setMyVehicleFitsWithProduct() {
    this.loadingVehicleCompatibility.set(true);

    try {
      const defaultVehicle = this.vehicleService.getDefaultVehicle();
      if (!defaultVehicle) {
        this.loadingVehicleCompatibility.set(false);
        this.myVehicleFitsWithProduct.set(false);
        return;
      }

      const product = this.product();
      if (!product) {
        this.loadingVehicleCompatibility.set(false);
        this.myVehicleFitsWithProduct.set(false);
        return;
      }

      const idGrupo = this.breadcrumbs().find(
        (breadcrumb) => breadcrumb.slug === this.productCategorySlug()
      )?.id;

      if (!idGrupo) {
        this.loadingVehicleCompatibility.set(false);
        this.myVehicleFitsWithProduct.set(false);
        return;
      }

      const fits = await firstValueFrom(
        this.productService.verifyProductFitsVehicle(
          product.part_number,
          defaultVehicle.idSubmodeloMotor || -1,
          idGrupo
        )
      );

      this.myVehicleFitsWithProduct.set(fits);
      this.loadingVehicleCompatibility.set(false);
    } catch (error) {
    } finally {
      this.loadingVehicleCompatibility.set(false);
    }
  }

  private handleProductNotFound() {
    this.product.set(null);
    // Agregar meta tag para que el servidor SSR devuelva 404
    this.document.head.insertAdjacentHTML(
      'beforeend',
      '<meta name="x-not-found" content="true">'
    );
    this.titleService.setTitle('Producto no encontrado - Allas Repuestos');
    this.metaService.updateTag({
      name: 'description',
      content: 'El producto que buscas no existe o ha sido removido.',
    });
    this.metaService.updateTag({
      name: 'robots',
      content: 'noindex, nofollow',
    });
  }

  private handleProductError(error: any) {
    this.product.set(null);
    // Agregar meta tag para que el servidor SSR devuelva 404
    this.document.head.insertAdjacentHTML(
      'beforeend',
      '<meta name="x-not-found" content="true">'
    );
    this.titleService.setTitle('Producto no encontrado - Allas Repuestos');
    this.metaService.updateTag({
      name: 'description',
      content: 'El producto que buscas no existe o ha sido removido.',
    });
    this.metaService.updateTag({
      name: 'robots',
      content: 'noindex, nofollow',
    });
  }

  private setInitialImage(product: Producto) {
    if (this.productUtilsService.hasImages(product)) {
      this.selectedImage.set(this.productUtilsService.getFirstImage(product));
    }
  }

  // Public methods for template
  selectImage(imageUrl: string) {
    this.selectedImage.set(imageUrl);
  }

  selectSize(size: string) {
    this.selectedSize.set(size);
  }

  increaseQuantity() {
    this.quantity.update((q) => q + 1);
  }

  decreaseQuantity() {
    if (this.quantity() > 1) {
      this.quantity.update((q) => q - 1);
    }
  }

  addToCart() {
    const currentProduct = this.product();
    if (currentProduct) {
      this.cartService.addItemToCart({
        producto: currentProduct,
        cantidad: this.quantity(),
      });
      this.notificationService.showSuccess(
        `${currentProduct.nombre_web} agregado al carrito`,
        'Producto disponible para cotización'
      );
    }
  }

  goBack() {
    this.router.navigate(['/repuestos']);
  }

  // Template utility methods
  getIntFormattedPrice(product: Producto): string {
    return this.priceFormattingService.formatIntegerPrice(product.precio);
  }

  getOnlyDecimalPrice(product: Producto): string {
    return this.priceFormattingService.getDecimalPrice(product.precio);
  }

  getSumInventory(product: Producto): number {
    return this.productUtilsService.getTotalInventory(product);
  }

  private loadBreadcrumbs(product: Producto) {
    const categorySlug = getProductCategorySlug(product);
    if (categorySlug) {
      const breadcrumbs = this.categoriesService.getCategoryPath(categorySlug);
      this.breadcrumbs.set(breadcrumbs);
    }
  }

  // Funciones de compartir
  getProductUrl(): string {
    if (typeof window !== 'undefined') {
      return window.location.href;
    }
    return '';
  }

  getProductShareText(): string {
    const currentProduct = this.product();
    if (!currentProduct) return '';
    return `¡Mira este producto! ${currentProduct.nombre_web}`;
  }

  shareOnWhatsApp() {
    const text = encodeURIComponent(this.getProductShareText());
    const url = encodeURIComponent(this.getProductUrl());
    window.open(`https://wa.me/?text=${text}%20${url}`, '_blank');
    this.showShareDialog = false;
  }

  shareOnFacebook() {
    const url = encodeURIComponent(this.getProductUrl());
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      '_blank'
    );
    this.showShareDialog = false;
  }

  shareOnMessenger() {
    const url = encodeURIComponent(this.getProductUrl());
    window.open(
      `https://www.facebook.com/dialog/send?link=${url}&app_id=YOUR_APP_ID&redirect_uri=${url}`,
      '_blank'
    );
    this.showShareDialog = false;
  }

  shareOnTwitter() {
    const text = encodeURIComponent(this.getProductShareText());
    const url = encodeURIComponent(this.getProductUrl());
    window.open(
      `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      '_blank'
    );
    this.showShareDialog = false;
  }

  async copyLink() {
    const url = this.getProductUrl();
    try {
      await navigator.clipboard.writeText(url);
      this.linkCopied.set(true);
      this.notificationService.showSuccess(
        'Enlace copiado',
        'El enlace del producto ha sido copiado al portapapeles'
      );

      // Restablecer el estado después de 2 segundos
      setTimeout(() => {
        this.linkCopied.set(false);
      }, 2000);
    } catch (err) {
      console.error('Error al copiar:', err);
      this.notificationService.showError(
        'Error',
        'No se pudo copiar el enlace'
      );
    }
  }
}
